import React, { useState } from 'react';
import jsPDF from 'jspdf';
import s3Service from '../services/s3Service.js';

// Get licensing fee from environment variable with fallback
const LICENSING_FEE = process.env.REACT_APP_PARTNERSHIP_LICENSING_FEE || '$5000';

// Get referral fee from environment variable with fallback
const REFERRAL_FEE = process.env.REACT_APP_PARTNERSHIP_REFERRAL_FEE_PERCENTAGE || '0.50';

const PRODUCTS = ["Working Capital", "Commercial Real Estate", "Non-profit Financing", "Equipment Financing"];

// Partnership Agreement Document Text Constants
const PARTNERSHIP_AGREEMENT_CONSTANTS = {
  HEADER: {
    TITLE: 'STRATEGIC PARTNERSHIP AGREEMENT',
    MODAL_TITLE: 'Partnership Agreement',
    STEP_LABEL: 'Review and Agree to Partnership Terms'
  },
  SECTIONS: {
    PARTIES: {
      TITLE: '1. PARTIES',
      CONTENT: [
        'This Partnership Agreement ("Agreement") is entered into by and between HalalCred LLC, a Washington State Limited Liability Company ("HalalCred"), and the Partner identified above (collectively referred to as "Parties").',
        'HalalCred operates as a listing platform serving businesses and non-profit organizations seeking Islamic financing and business services. HalalCred also assists financial institutions in launching and offering Islamic financing products. This Agreement governs the terms under which Partner will provide services through the HalalCred platform.'
      ]
    },
    SCOPE_OF_PARTNERSHIP: {
      TITLE: '2. SCOPE OF PARTNERSHIP',
      SUBSECTIONS: [
        {
          TITLE: '2.1 Services Provided.',
          CONTENT: 'Partner shall provide the following Islamic financing products to HalalCred users:',
          ITEMS: PRODUCTS.map(product => `${product}`)
        },
        {
          TITLE: '2.2 Service Delivery.',
          CONTENT: "Partner shall deliver services in accordance with industry standards and Shariah compliance principles as verified by HalalCred's Shariah advisory board."
        },
        {
          TITLE: '2.3 Listing.',
          CONTENT: "HalalCred shall list Partner as an approved service provider on its platform, making Partner's services available to HalalCred users."
        },
        {
          TITLE: '2.4 Referrals.',
          CONTENT: "HalalCred will refer qualified users seeking services within Partner's scope to Partner for evaluation and potential engagement."
        },
        {
          TITLE: '2.5 Non-Exclusivity.',
          CONTENT: "This Agreement is non-exclusive. HalalCred retains complete discretion to add, approve, and list any other partners or service providers offering similar or identical services on its platform at any time. Nothing in this Agreement shall be construed as restricting or preventing HalalCred from exercising this right."
        }
      ]
    },
    TERM: {
      TITLE: '3. TERM',
      SUBSECTIONS: [
        {
          TITLE: '3.1 Initial Term.',
          CONTENT: 'This Agreement shall commence on the Effective Date and continue for a period of one (1) year.'
        },
        {
          TITLE: '3.2 Renewal.',
          CONTENT: 'Upon expiration of the Initial Term, this Agreement may be renewed for additional one-year terms upon mutual written agreement of the Parties.'
        }
      ]
    },
    FEE_STRUCTURE: {
      TITLE: '4. FEE STRUCTURE AND PAYMENT',
      SUBSECTIONS: [
        {
          TITLE: '4.1 Fee Structure.',
          CONTENT: `Partner shall pay HalalCred a non-refundable base licensing fee of ${LICENSING_FEE}, payable upon invoice. Additionally, Partner shall pay a variable platform fee equal to ${REFERRAL_FEE}% of the total principal amount disbursed for all successful application loans funded by Partner during the term of this Agreement, regardless of the lead source. A "successful application" is defined as any loan for which the borrower receives approval and the fund transfer is completed. This fee structure is subject to review and mutual adjustment upon Agreement renewal.`
        },
        {
          TITLE: '4.2 Taxes.',
          CONTENT: 'Each party shall be responsible for their own taxes arising from this Agreement.'
        }
      ]
    },
    DATA_CLASSIFICATION: {
      TITLE: '5. DATA CLASSIFICATION AND PRIVACY',
      SUBSECTIONS: [
        {
          TITLE: '5.1 Data Classification.',
          CONTENT: "All customer data shared by HalalCred with Partner shall be treated as 'Critical - Confidential' in accordance with industry best practices for data protection."
        },
        {
          TITLE: '5.2 Data Usage.',
          CONTENT: 'Partner agrees to use this data exclusively for the purpose of evaluating and processing applications for services.'
        },
        {
          TITLE: '5.3 Prohibited Uses.',
          CONTENT: 'Under no circumstances shall Partner store, share, sell, or reuse this data for any unrelated marketing, commercial, or non-financial purpose.'
        },
        {
          TITLE: '5.4 Data Security.',
          CONTENT: 'Partner shall implement and maintain appropriate technical, organizational, and physical safeguards to protect the security and confidentiality of all data received from HalalCred or its users.'
        },
        {
          TITLE: '5.5 Data Breach Notification.',
          CONTENT: 'Partner shall notify HalalCred within 24 hours of discovering any data breach or unauthorized access to HalalCred user data.'
        }
      ]
    },
    COMPLIANCE: {
      TITLE: '6. COMPLIANCE AND AUDITING',
      SUBSECTIONS: [
        {
          TITLE: '6.1 Audit Rights.',
          CONTENT: 'HalalCred reserves the right to request financial documentation and compliance records from Partner at any time during the term of this Agreement with reasonable notice. Additionally, HalalCred shall have the right, once per calendar year and upon reasonable notice of not less than ten (10) business days, to audit Partner\'s financial records related to loan disbursements to verify the accuracy of variable platform fees paid pursuant to Section 4.1. Such audits may be conducted by HalalCred or its designated representatives during Partner\'s normal business hours.'
        },
        {
          TITLE: '6.2 Cooperation.',
          CONTENT: 'Partner shall provide reasonable access to such records and cooperate fully with any Shariah compliance audits initiated by HalalCred or its advisory board.'
        },
        {
          TITLE: '6.3 Audit Frequency.',
          CONTENT: "Audits may be conducted yearly or as deemed necessary by HalalCred's Shariah board with at least five (5) business days' notice."
        },
        {
          TITLE: '6.4 Audit Costs.',
          CONTENT: 'Each party shall bear their own costs related to such audits unless the audit reveals material non-compliance, in which case the Partner shall bear the reasonable costs of the audit.'
        }
      ]
    },
    SHARIAH_COMPLIANCE: {
      TITLE: '7. SHARIAH COMPLIANCE',
      SUBSECTIONS: [
        {
          TITLE: '7.1 Compliance Requirement.',
          CONTENT: 'Maintaining full compliance with Islamic financing principles is a mandatory condition of this Agreement.'
        },
        {
          TITLE: '7.2 Non-Compliance.',
          CONTENT: 'If Partner is found to be in violation of Shariah compliance, HalalCred reserves the right to:',
          ITEMS: [
            'Issue a written notice outlining the non-compliance',
            'Temporarily suspend Partner from the HalalCred platform',
            'Terminate this Agreement subject to the process outlined in Section 7.3'
          ]
        },
        {
          TITLE: '7.3 Remediation Process.',
          CONTENT: "Partner will have the opportunity to respond within seven (7) business days of receiving a non-compliance notice and provide a remediation plan. HalalCred's Shariah board will review the response and make a final determination."
        },
        {
          TITLE: '7.4 Final Authority.',
          CONTENT: 'HalalCred retains final decision-making authority regarding Shariah compliance matters related to its platform.'
        }
      ]
    },
    TERMINATION: {
      TITLE: '8. TERMINATION',
      SUBSECTIONS: [
        {
          TITLE: '8.1 Termination for Cause.',
          CONTENT: 'Either party may terminate this Agreement for cause if the other party materially breaches this Agreement and fails to cure such breach within fifteen (15) days after receipt of written notice.'
        },
        {
          TITLE: '8.2 Immediate Termination.',
          CONTENT: 'HalalCred may immediately terminate this Agreement if:',
          ITEMS: [
            'Partner engages in any illegal activity',
            'Partner violates Shariah compliance principles and fails to remediate as outlined in Section 7',
            'Partner breaches data privacy or confidentiality obligations',
            'Partner makes false representations regarding qualifications, certifications, or credentials',
            'Partner becomes subject to negative regulatory action or loses required professional licenses'
          ]
        },
        {
          TITLE: '8.3 Termination for Quality Issues.',
          CONTENT: 'HalalCred may terminate this Agreement with five (5) days written notice if:',
          ITEMS: [
            'Partner experiences a pattern of legitimate user complaints related to service quality, as reasonably determined by HalalCred',
            'Partner fails to maintain professional standards or demonstrates a pattern of unprofessional conduct with users'
          ]
        },
        {
          TITLE: '8.4 Termination for Reputational Risk.',
          CONTENT: 'HalalCred may terminate this Agreement with immediate effect if Partner:',
          ITEMS: [
            "Engages in conduct that may reasonably damage HalalCred's reputation or brand",
            'Is involved in public scandal or controversy that may reflect negatively on HalalCred by association',
            'Makes public statements contradicting or undermining Shariah compliance principles',
            'Misrepresents its relationship with HalalCred to clients or third parties'
          ]
        },
        {
          TITLE: '8.5 Termination for Business Changes.',
          CONTENT: 'Either party may terminate this Agreement with fifteen (15) days written notice if:',
          ITEMS: [
            'Partner undergoes a change of control or ownership',
            'Partner ceases to offer the services specified in Section 2.1',
            'Partner enters bankruptcy or insolvency proceedings',
            'HalalCred discontinues the platform or the particular service category'
          ]
        },
        {
          TITLE: '8.6 Termination Without Cause.',
          CONTENT: 'Either party may terminate this Agreement without cause upon thirty (30) days written notice to the other party.'
        },
        {
          TITLE: '8.7 Effect of Termination.',
          CONTENT: 'Upon termination:',
          ITEMS: [
            'Partner shall be removed from the HalalCred platform',
            'Partner shall return or securely destroy all HalalCred user data',
            'Partner shall complete any in-progress services for existing clients unless legally or ethically prohibited',
            'Sections 5, 9, 10, 12, and 13 shall survive termination'
          ]
        },
        {
          TITLE: '8.8 Transition Assistance.',
          CONTENT: "Upon termination for any reason other than Partner's material breach, Partner shall provide reasonable transition assistance to HalalCred and affected users for a period of thirty (30) days to ensure continuity of service."
        }
      ]
    },
    CONFIDENTIALITY: {
      TITLE: '9. CONFIDENTIALITY',
      SUBSECTIONS: [
        {
          TITLE: '9.1 Confidential Information.',
          CONTENT: 'Each party may disclose certain confidential and proprietary information to the other. "Confidential Information" includes all information marked confidential or that would reasonably be considered confidential given the nature of the information and the circumstances of disclosure.'
        },
        {
          TITLE: '9.2 Protection.',
          CONTENT: 'Each party agrees to:',
          ITEMS: [
            "Hold the other's Confidential Information in strict confidence",
            'Not disclose such Confidential Information to any third party except as required by law',
            'Use such Confidential Information only for purposes of performing under this Agreement',
            'Protect such Confidential Information with at least the same degree of care used to protect its own confidential information'
          ]
        },
        {
          TITLE: '9.3 Exceptions.',
          CONTENT: 'Confidentiality obligations do not apply to information that:',
          ITEMS: [
            'Is or becomes publicly available through no fault of the receiving party',
            'Was rightfully known to the receiving party prior to receipt from the disclosing party',
            'Is rightfully obtained from a third party without restriction',
            'Is independently developed without use of the disclosing party\'s Confidential Information'
          ]
        },
        {
          TITLE: '9.4 Duration.',
          CONTENT: 'These confidentiality obligations shall survive termination of this Agreement for a period of three (3) years.'
        }
      ]
    },
    INTELLECTUAL_PROPERTY: {
      TITLE: '10. INTELLECTUAL PROPERTY',
      SUBSECTIONS: [
        {
          TITLE: '10.1 Ownership.',
          CONTENT: 'Each party retains all rights, title, and interest in and to its own pre-existing intellectual property.'
        },
        {
          TITLE: '10.2 License.',
          CONTENT: "HalalCred grants Partner a limited, non-exclusive, non-transferable license to use HalalCred's name, logo, and other designations solely for the purpose of identifying Partner as an approved service provider on the HalalCred platform during the term of this Agreement."
        },
        {
          TITLE: '10.3 Partner Brand Usage.',
          CONTENT: "Partner grants HalalCred a limited, non-exclusive, royalty-free license to use Partner's name, logo, trademark, and other brand elements for marketing purposes, including but not limited to social media campaigns, advertisements, promotional materials, press releases, and public relations initiatives. HalalCred may reference Partner by name in media interviews, public statements, and testimonials related to the HalalCred platform. HalalCred agrees to comply with any reasonable brand guidelines provided by Partner and shall not present Partner's brand elements in a manner that could be deemed detrimental to Partner's brand image. Partner may revoke this permission for specific uses with written notice if such uses are demonstrably harmful to Partner's brand, but may not unreasonably withhold permission for standard marketing activities."
        },
        {
          TITLE: '10.4 Restrictions.',
          CONTENT: 'Partner shall not:',
          ITEMS: [
            "Use HalalCred's intellectual property in any manner that might tarnish, disparage, or reflect adversely on HalalCred",
            "Challenge or assist others in challenging HalalCred's intellectual property rights",
            'Register or attempt to register any trademarks, trade names, or domain names that are confusingly similar to HalalCred\'s'
          ]
        }
      ]
    },
    REPRESENTATIONS: {
      TITLE: '11. REPRESENTATIONS AND WARRANTIES',
      SUBSECTIONS: [
        {
          TITLE: '11.1 Mutual Representations.',
          CONTENT: 'Each party represents and warrants that:',
          ITEMS: [
            'It has full power and authority to enter into this Agreement',
            'Its performance will not violate any other agreement to which it is a party',
            'It will comply with all applicable laws and regulations'
          ]
        },
        {
          TITLE: '11.2 Partner Warranties.',
          CONTENT: 'Partner further represents and warrants that:',
          ITEMS: [
            'All services will be performed in a professional manner consistent with industry standards',
            'Partner possesses all necessary licenses, certifications, and qualifications to provide the services',
            'Partner shall maintain adequate insurance coverage for its operations'
          ]
        },
        {
          TITLE: '11.3 HalalCred Warranties.',
          CONTENT: 'HalalCred further represents and warrants that:',
          ITEMS: [
            'It has the right to operate the HalalCred platform',
            'It will provide Partner with reasonable support to facilitate service delivery to users'
          ]
        }
      ]
    },
    INDEMNIFICATION: {
      TITLE: '12. INDEMNIFICATION',
      SUBSECTIONS: [
        {
          TITLE: '12.1 Partner Indemnification.',
          CONTENT: 'Partner shall defend, indemnify, and hold harmless HalalCred and its officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys\' fees) arising out of or relating to:',
          ITEMS: [
            "Partner's breach of this Agreement",
            "Partner's negligence or willful misconduct",
            "Any claim that Partner's services infringe upon the rights of a third party",
            'Any claim by a user arising from services provided by Partner'
          ]
        },
        {
          TITLE: '12.2 HalalCred Indemnification.',
          CONTENT: 'HalalCred shall defend, indemnify, and hold harmless Partner and its officers, directors, employees, and agents from and against any claims, damages, losses, liabilities, costs, and expenses (including reasonable attorneys\' fees) arising out of or relating to:',
          ITEMS: [
            "HalalCred's breach of this Agreement",
            "HalalCred's negligence or willful misconduct",
            'Any claim that the HalalCred platform infringes upon the rights of a third party'
          ]
        },
        {
          TITLE: '12.3 Procedure.',
          CONTENT: 'The indemnified party shall: (a) promptly notify the indemnifying party in writing of any claim; (b) give the indemnifying party sole control of the defense and settlement of the claim; and (c) provide reasonable cooperation in the defense and settlement of the claim.'
        }
      ]
    },
    LIMITATION_OF_LIABILITY: {
      TITLE: '13. LIMITATION OF LIABILITY',
      SUBSECTIONS: [
        {
          TITLE: '13.1 Exclusion of Damages.',
          CONTENT: 'NEITHER PARTY SHALL BE LIABLE TO THE OTHER FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO THIS AGREEMENT.'
        },
        {
          TITLE: '13.2 Liability Cap.',
          CONTENT: 'EACH PARTY\'S TOTAL CUMULATIVE LIABILITY TO THE OTHER UNDER THIS AGREEMENT SHALL NOT EXCEED THE GREATER OF (A) $10,000 OR (B) THE TOTAL FEES PAID OR PAYABLE BY PARTNER TO HALALCRED DURING THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY.'
        },
        {
          TITLE: '13.3 Exceptions.',
          CONTENT: 'The limitations in Sections 13.1 and 13.2 shall not apply to:',
          ITEMS: [
            'Breaches of confidentiality obligations',
            'Indemnification obligations',
            'Violations of intellectual property rights',
            'Gross negligence or willful misconduct'
          ]
        }
      ]
    },
    RECORD_RETENTION: {
      TITLE: '14. RECORD RETENTION',
      SUBSECTIONS: [
        {
          TITLE: '14.1 Partner Records.',
          CONTENT: 'Partner shall maintain all documents and records related to services provided under this Agreement for a minimum of three (3) years following service completion.'
        },
        {
          TITLE: '14.2 HalalCred Records.',
          CONTENT: 'HalalCred shall maintain records of all referrals and related communications for a period of three (3) years.'
        }
      ]
    },
    COMMUNICATION: {
      TITLE: '15. COMMUNICATION',
      SUBSECTIONS: [
        {
          TITLE: '15.1 Notices.',
          CONTENT: 'All notices required or permitted under this Agreement shall be in writing and delivered by:',
          ITEMS: [
            'Personal delivery',
            'Nationally recognized overnight courier',
            'Certified mail, return receipt requested',
            'Email with confirmation of receipt'
          ]
        },
        {
          TITLE: '15.2 Addresses.',
          CONTENT: 'Notices shall be sent to the addresses set forth above or to such other addresses as either party may specify in writing.'
        },
        {
          TITLE: '15.3 Electronic Communications.',
          CONTENT: 'The parties may use email, mobile phones, and other electronic means for day-to-day communications. Each party acknowledges the inherent security risks in electronic communications and consents to their use.'
        }
      ]
    },
    DISPUTE_RESOLUTION: {
      TITLE: '16. DISPUTE RESOLUTION',
      SUBSECTIONS: [
        {
          TITLE: '16.1 Mediation.',
          CONTENT: 'In the event of any dispute, claim, or controversy arising out of or relating to this Agreement, the parties agree to first attempt to resolve the matter through mediation. The mediation shall take place in King County, Washington.'
        },
        {
          TITLE: '16.2 Arbitration.',
          CONTENT: 'If mediation fails to resolve the dispute within thirty (30) days, the parties agree to submit the dispute to binding arbitration in accordance with the rules of the American Arbitration Association. The arbitration shall take place in King County, Washington, and the arbitrator\'s decision shall be final and binding.'
        },
        {
          TITLE: '16.3 Costs.',
          CONTENT: 'Each party shall bear their own costs and attorney fees associated with the mediation and arbitration.'
        },
        {
          TITLE: '16.4 Exceptions.',
          CONTENT: 'Either party may seek injunctive or other equitable relief in any court of competent jurisdiction to protect its intellectual property rights or confidential information pending resolution of the dispute.'
        }
      ]
    },
    GENERAL_PROVISIONS: {
      TITLE: '17. GENERAL PROVISIONS',
      SUBSECTIONS: [
        {
          TITLE: '17.1 Relationship.',
          CONTENT: 'This Agreement does not create a joint venture, agency, partnership, or employment relationship between the parties. Neither party has the authority to bind the other or to incur obligations on the other\'s behalf.'
        },
        {
          TITLE: '17.2 Assignment.',
          CONTENT: 'Neither party may assign this Agreement without the prior written consent of the other party, except that HalalCred may assign this Agreement to a successor in connection with a merger, acquisition, or sale of all or substantially all of its assets.'
        },
        {
          TITLE: '17.3 Force Majeure.',
          CONTENT: 'Neither party shall be liable for any failure or delay in performance due to circumstances beyond its reasonable control, including acts of God, natural disasters, terrorism, riots, war, pandemics, labor disputes, or governmental actions.'
        },
        {
          TITLE: '17.4 Modifications.',
          CONTENT: 'This Agreement may only be modified by a written amendment signed by authorized representatives of both parties.'
        },
        {
          TITLE: '17.5 Waiver.',
          CONTENT: 'The failure of either party to enforce any provision of this Agreement shall not constitute a waiver of future enforcement of that or any other provision.'
        },
        {
          TITLE: '17.6 Severability.',
          CONTENT: 'If any provision of this Agreement is held to be unenforceable, the remaining provisions shall remain in full force and effect.'
        },
        {
          TITLE: '17.7 Entire Agreement.',
          CONTENT: 'This Agreement constitutes the entire agreement between the parties with respect to its subject matter and supersedes all prior or contemporaneous communications, representations, or agreements, whether oral or written.'
        },
        {
          TITLE: '17.8 Governing Law.',
          CONTENT: 'This Agreement and all related matters shall be governed by the laws of the State of Washington, without regard to its conflict of law provisions.'
        },
        {
          TITLE: '17.9 Counterparts.',
          CONTENT: 'This Agreement may be executed in counterparts, each of which shall be deemed an original, but all of which together shall constitute one and the same instrument.'
        }
      ]
    },
    ACCEPTANCE: {
      TITLE: '18. ACCEPTANCE',
      CONTENT: 'IN WITNESS WHEREOF, the parties have executed this Agreement as of the Effective Date first written above.'
    },
    AGREEMENT: {
      TITLE: 'AGREEMENT',
      ACKNOWLEDGMENT: 'I acknowledge that I have read, understood, and agree to be bound by the terms and conditions of this Strategic Partnership Agreement.',
      AUTHORITY_CONFIRMATION: 'By checking this box, I confirm that I have the authority to enter into this agreement on behalf of'
    },
    SIGNATURES: {
      TITLE: '11. Signatures',
      COMPANY_NAME: 'HalalCred',
      LABELS: {
        DATE: 'Date'
      }
    }
  },
  CONTACT: {
    TITLE: 'Contact Information',
    INTRO: 'For questions about this partnership agreement, please contact:',
    COMPANY_NAME: 'HalalCred Partnership Team',
    EMAIL: 'salaam@halalcred.com',
    PHONE: '+1-425-340-0980'
  }
};

const PartnershipAgreementModal = ({ isOpen, onClose, partnerName = "[Partner Name]", onStepComplete, existingDocument = null }) => {
  if (!isOpen) return null;

  // PDF generation setup
  const [isAgreed, setIsAgreed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');

  // Generate current date
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Template-based PDF generation using jsPDF text rendering
  const generatePDFFromTemplate = () => {
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = 210;
    const pageHeight = 297;
    const margin = 20;
    let yPosition = margin;

    // Helper function to add a new page if needed
    const checkPageBreak = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
        return true;
      }
      return false;
    };

    // Helper function to add text with word wrap
    const addText = (text, fontSize = 11, isBold = false, align = 'left') => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      
      const maxWidth = pageWidth - (margin * 2);
      const lines = pdf.splitTextToSize(text, maxWidth);
      const singleLineHeight = fontSize * 0.35;
      
      lines.forEach((line) => {
        if (yPosition + singleLineHeight > pageHeight - margin) {
          pdf.addPage();
          yPosition = margin;
        }
        pdf.text(line, margin, yPosition, { align });
        yPosition += singleLineHeight;
      });
    };

    // Helper function to add a section
    const addSection = (title, content, listItems = null, subsections = null) => {
      checkPageBreak(15);
      addText(title, 14, true);
      yPosition += 2;
      
      if (subsections && Array.isArray(subsections)) {
        subsections.forEach((subsection) => {
          if (subsection.TITLE) {
            addText(subsection.TITLE, 11, true);
            yPosition += 1;
          }
          if (subsection.CONTENT) {
            addText(subsection.CONTENT, 11, false);
            yPosition += 1;
          }
          if (subsection.ITEMS && Array.isArray(subsection.ITEMS)) {
            subsection.ITEMS.forEach((item) => {
              addText(`• ${item}`, 11, false);
            });
            yPosition += 1;
          }
        });
      } else {
        if (Array.isArray(content)) {
          content.forEach((para) => {
            addText(para, 11, false);
            yPosition += 1;
          });
        } else if (content) {
          addText(content, 11, false);
          yPosition += 1;
        }
        
        if (listItems && Array.isArray(listItems)) {
          listItems.forEach((item) => {
            addText(`• ${item}`, 11, false);
          });
          yPosition += 1;
        }
      }
      
      yPosition += 4;
    };

    // Header
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text(PARTNERSHIP_AGREEMENT_CONSTANTS.HEADER.TITLE, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;
    
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`Effective Date: ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 5;
    pdf.text(`Parties: ${PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.COMPANY_NAME} and ${partnerName}`, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Draw a line
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 8;

    // Section 1
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.PARTIES.TITLE,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.PARTIES.CONTENT
    );

    // Section 2
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SCOPE_OF_PARTNERSHIP.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SCOPE_OF_PARTNERSHIP.SUBSECTIONS
    );

    // Section 3
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERM.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERM.SUBSECTIONS
    );

    // Section 4
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.FEE_STRUCTURE.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.FEE_STRUCTURE.SUBSECTIONS
    );

    // Section 5
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DATA_CLASSIFICATION.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DATA_CLASSIFICATION.SUBSECTIONS
    );

    // Section 6
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMPLIANCE.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMPLIANCE.SUBSECTIONS
    );

    // Section 7
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SHARIAH_COMPLIANCE.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SHARIAH_COMPLIANCE.SUBSECTIONS
    );

    // Section 8
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERMINATION.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERMINATION.SUBSECTIONS
    );

    // Section 9
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.CONFIDENTIALITY.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.CONFIDENTIALITY.SUBSECTIONS
    );

    // Section 10
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INTELLECTUAL_PROPERTY.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INTELLECTUAL_PROPERTY.SUBSECTIONS
    );

    // Section 11
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.REPRESENTATIONS.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.REPRESENTATIONS.SUBSECTIONS
    );

    // Section 12
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INDEMNIFICATION.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INDEMNIFICATION.SUBSECTIONS
    );

    // Section 13
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.LIMITATION_OF_LIABILITY.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.LIMITATION_OF_LIABILITY.SUBSECTIONS
    );

    // Section 14
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.RECORD_RETENTION.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.RECORD_RETENTION.SUBSECTIONS
    );

    // Section 15
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMMUNICATION.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMMUNICATION.SUBSECTIONS
    );

    // Section 16
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DISPUTE_RESOLUTION.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DISPUTE_RESOLUTION.SUBSECTIONS
    );

    // Section 17
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.GENERAL_PROVISIONS.TITLE,
      null,
      null,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.GENERAL_PROVISIONS.SUBSECTIONS
    );

    // Section 18
    checkPageBreak(20);
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.ACCEPTANCE.TITLE,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.ACCEPTANCE.CONTENT
    );

    // Agreement Section
    checkPageBreak(20);
    addSection(
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.AGREEMENT.TITLE,
      PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.AGREEMENT.ACKNOWLEDGMENT
    );
    addText(`${PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.AGREEMENT.AUTHORITY_CONFIRMATION} ${partnerName} and that all information provided is accurate and complete.`, 10, false);
    yPosition += 2;
    addText(`Status: ${isAgreed ? 'AGREED' : 'NOT AGREED'}`, 11, true);
    yPosition += 8;

    // Section 11 - Signatures
    checkPageBreak(60);
    addText(PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.TITLE, 14, true);
    yPosition += 8;

    // Signature boxes
    const sigBoxWidth = (pageWidth - (margin * 2) - 10) / 2;
    const sigBoxY = yPosition;

    // Left signature box (HalalCred)
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text(PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.COMPANY_NAME, margin, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.LABELS.DATE, margin, yPosition);
    yPosition += 4;
    pdf.text(currentDate, margin, yPosition);
    yPosition += 8;

    // Right signature box (Partner)
    yPosition = sigBoxY;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    const partnerNameLines = pdf.splitTextToSize(partnerName, sigBoxWidth);
    pdf.text(partnerNameLines, margin + sigBoxWidth + 10, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.LABELS.DATE, margin + sigBoxWidth + 10, yPosition);
    yPosition += 4;
    pdf.text(currentDate, margin + sigBoxWidth + 10, yPosition);
    yPosition += 12;

    // Contact Information
    checkPageBreak(30);
    addText(PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.TITLE, 12, true);
    yPosition += 4;
    addText(PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.INTRO, 11, false);
    yPosition += 2;
    addText(PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.COMPANY_NAME, 11, true);
    addText(`Email: ${PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.EMAIL}`, 11, false);
    addText(`Phone: ${PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.PHONE}`, 11, false);

    return pdf;
  };

  // Handle PDF generation using template approach
  const handleGeneratePDF = async () => {
    if (!isAgreed) {
      alert('Please agree to the terms and conditions before generating the PDF.');
      return;
    }

    try {
      // Show loading state
      const button = document.querySelector('[data-partnership-pdf-button]');
      if (button) {
        button.disabled = true;
        button.textContent = 'Generating PDF...';
      }

      // Generate PDF from template
      const pdf = generatePDFFromTemplate();
      
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `Partnership_Agreement_${partnerName.replace(/\s+/g, '_')}_${timestamp}.pdf`;
      
      // Download the PDF locally
      pdf.save(filename);
      
      // Upload to S3
      setIsUploading(true);
      setUploadStatus('Uploading PDF to secure storage...');
      
      try {
        // Convert PDF to blob for S3 upload
        const pdfBlob = pdf.output('blob');
        const pdfFile = new File([pdfBlob], filename, { type: 'application/pdf' });
        
        // Get partner ID for S3 upload
        const userId = localStorage.getItem('userId') || 'demo-user-id';
        
        // Upload to S3
        const uploadResult = await s3Service.uploadFile(pdfFile, userId, 'partnership');
        
        setUploadStatus('PDF uploaded successfully to secure storage');
        
        // Complete the step and move to next
        if (onStepComplete) {
          onStepComplete({
            step: 'partnership_agreement',
            completed: true,
            timestamp: new Date().toISOString(),
            filename: filename,
            partnerName: partnerName,
            s3Key: uploadResult.s3Key,
            s3Url: uploadResult.s3Url,
            documentPath: uploadResult.s3Key
          });
          console.log('Partnership Modal - onStepComplete called successfully');
        } else {
          console.error('Partnership Modal - onStepComplete is not provided!');
        }
        
        // Show success message with better UX
        const successMessage = document.createElement('div');
        successMessage.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        successMessage.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
          </svg>
          <span>Partnership Agreement generated and uploaded successfully! Step completed.</span>
        `;
        document.body.appendChild(successMessage);
        setTimeout(() => {
          document.body.removeChild(successMessage);
        }, 4000);
        
        // Close the modal after successful completion
        setTimeout(() => {
          onClose();
        }, 1000);
        
      } catch (uploadError) {
        console.error('S3 upload failed:', uploadError);
        console.log('Partnership Modal - S3 upload failed, NOT completing step to preserve document');
        setUploadStatus('Upload failed - document saved locally only');
        
        // DO NOT complete the step if upload fails - we need the document saved!
        // The user can retry or contact support
        
        // Show error message with retry option
        const errorMessage = document.createElement('div');
        errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
        errorMessage.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
          </svg>
          <span>Upload failed! PDF saved locally. Please retry or contact support.</span>
        `;
        document.body.appendChild(errorMessage);
        setTimeout(() => {
          if (document.body.contains(errorMessage)) {
            document.body.removeChild(errorMessage);
          }
        }, 8000);
      } finally {
        setIsUploading(false);
        setUploadStatus('');
        
        // Reset button state
        if (button) {
          button.disabled = false;
          button.textContent = 'Complete Step & Generate PDF';
        }
        
        // Only close the modal if upload was successful (success case is handled above)
        // If upload failed, keep modal open so user can retry
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      // Show error message with better UX
      const errorMessage = document.createElement('div');
      errorMessage.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2';
      errorMessage.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
        </svg>
        <span>Failed to generate PDF. Please try again.</span>
      `;
      document.body.appendChild(errorMessage);
      setTimeout(() => {
        document.body.removeChild(errorMessage);
      }, 5000);
      
      // Reset button state
      const button = document.querySelector('[data-partnership-pdf-button]');
      if (button) {
        button.disabled = false;
        button.textContent = 'Complete Step & Generate PDF';
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{PARTNERSHIP_AGREEMENT_CONSTANTS.HEADER.MODAL_TITLE}</h2>
            <p className="text-sm text-gray-600 mt-1">{PARTNERSHIP_AGREEMENT_CONSTANTS.HEADER.STEP_LABEL}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="max-w-none">
            {/* Document Info */}
            <div className="mb-8 p-4 bg-emerald-50 rounded-lg">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Effective Date:</strong> {currentDate}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Parties:</strong> {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.COMPANY_NAME} and {partnerName}
              </p>
            </div>

            {/* Existing Document Section */}
            {existingDocument && (
              <div className="mb-8 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="text-lg font-semibold text-green-800 mb-3">📄 Completed Partnership Agreement</h3>
                <div className="space-y-2">
                  <p className="text-sm text-green-700">
                    <strong>Document:</strong> {existingDocument.filename || 'Partnership Agreement'}
                  </p>
                  <p className="text-sm text-green-700">
                    <strong>Completed:</strong> {existingDocument.completedAt ? new Date(existingDocument.completedAt).toLocaleDateString() : 'Unknown'}
                  </p>
                  {existingDocument.s3Key && (
                    <div className="mt-3 flex gap-2">
                      {existingDocument.s3Key && (
                        <button
                          onClick={async () => {
                            try {
                              const signedUrl = await s3Service.getSignedUrl(existingDocument.s3Key);
                              const link = document.createElement('a');
                              link.href = signedUrl;
                              link.download = existingDocument.filename || 'Partnership_Agreement.pdf';
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            } catch (error) {
                              console.error('Error downloading document:', error);
                              alert('Failed to download document. Please try the view link instead.');
                            }
                          }}
                          className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Partnership Agreement Content - Hide when completed */}
            {!existingDocument && (
              <>
            <section className="mb-10 p-6 bg-white border-l-4 border-emerald-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.PARTIES.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.PARTIES.CONTENT.map((para, index) => (
                <p key={index} className={`text-gray-800 leading-relaxed text-base ${index > 0 ? 'mt-4' : ''}`}>
                  {para}
                </p>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-blue-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SCOPE_OF_PARTNERSHIP.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SCOPE_OF_PARTNERSHIP.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SCOPE_OF_PARTNERSHIP.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base mb-4">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex} className="ml-6">{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-purple-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERM.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERM.SUBSECTIONS.map((subsection, index) => (
                <p key={index} className={`text-gray-800 leading-relaxed text-base ${index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERM.SUBSECTIONS.length - 1 ? 'mb-4' : ''}`}>
                  <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                </p>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-orange-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.FEE_STRUCTURE.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.FEE_STRUCTURE.SUBSECTIONS.map((subsection, index) => (
                <p key={index} className={`text-gray-800 leading-relaxed text-base ${index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.FEE_STRUCTURE.SUBSECTIONS.length - 1 ? 'mb-4' : ''}`}>
                  <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                </p>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-red-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DATA_CLASSIFICATION.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DATA_CLASSIFICATION.SUBSECTIONS.map((subsection, index) => (
                <p key={index} className={`text-gray-800 leading-relaxed text-base ${index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DATA_CLASSIFICATION.SUBSECTIONS.length - 1 ? 'mb-4' : ''}`}>
                  <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                </p>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-teal-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMPLIANCE.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMPLIANCE.SUBSECTIONS.map((subsection, index) => (
                <p key={index} className={`text-gray-800 leading-relaxed text-base ${index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMPLIANCE.SUBSECTIONS.length - 1 ? 'mb-4' : ''}`}>
                  <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                </p>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-indigo-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SHARIAH_COMPLIANCE.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SHARIAH_COMPLIANCE.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SHARIAH_COMPLIANCE.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base mb-4">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-pink-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERMINATION.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERMINATION.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.TERMINATION.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base mb-4">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-gray-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.CONFIDENTIALITY.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.CONFIDENTIALITY.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.CONFIDENTIALITY.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base mb-4">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-yellow-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INTELLECTUAL_PROPERTY.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INTELLECTUAL_PROPERTY.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INTELLECTUAL_PROPERTY.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-indigo-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.REPRESENTATIONS.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.REPRESENTATIONS.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.REPRESENTATIONS.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base mb-4">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-red-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INDEMNIFICATION.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INDEMNIFICATION.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.INDEMNIFICATION.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base mb-4">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-purple-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.LIMITATION_OF_LIABILITY.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.LIMITATION_OF_LIABILITY.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.LIMITATION_OF_LIABILITY.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-teal-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.RECORD_RETENTION.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.RECORD_RETENTION.SUBSECTIONS.map((subsection, index) => (
                <p key={index} className={`text-gray-800 leading-relaxed text-base ${index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.RECORD_RETENTION.SUBSECTIONS.length - 1 ? 'mb-4' : ''}`}>
                  <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                </p>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-blue-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMMUNICATION.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMMUNICATION.SUBSECTIONS.map((subsection, index) => (
                <div key={index} className={index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.COMMUNICATION.SUBSECTIONS.length - 1 ? 'mb-4' : ''}>
                  <p className="text-gray-800 leading-relaxed text-base mb-4">
                    <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                  </p>
                  {subsection.ITEMS && (
                    <ul className="list-disc pl-6 text-gray-800 space-y-2 text-base mb-4">
                      {subsection.ITEMS.map((item, itemIndex) => (
                        <li key={itemIndex}>{item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-orange-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DISPUTE_RESOLUTION.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DISPUTE_RESOLUTION.SUBSECTIONS.map((subsection, index) => (
                <p key={index} className={`text-gray-800 leading-relaxed text-base ${index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.DISPUTE_RESOLUTION.SUBSECTIONS.length - 1 ? 'mb-4' : ''}`}>
                  <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                </p>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-pink-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.GENERAL_PROVISIONS.TITLE}</h2>
              {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.GENERAL_PROVISIONS.SUBSECTIONS.map((subsection, index) => (
                <p key={index} className={`text-gray-800 leading-relaxed text-base ${index < PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.GENERAL_PROVISIONS.SUBSECTIONS.length - 1 ? 'mb-4' : ''}`}>
                  <strong>{subsection.TITLE}</strong> {subsection.CONTENT}
                </p>
              ))}
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-gray-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.ACCEPTANCE.TITLE}</h2>
              <p className="text-gray-800 leading-relaxed text-base">
                {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.ACCEPTANCE.CONTENT}
              </p>
            </section>

            <section className="mb-10 p-6 bg-green-50 border-l-4 border-green-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.AGREEMENT.TITLE}</h2>
              <div className="mb-6 p-4 bg-white rounded-lg border shadow-sm">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="partnership-agreement"
                    checked={isAgreed}
                    onChange={(e) => setIsAgreed(e.target.checked)}
                    className="mt-1 h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="partnership-agreement" className="text-gray-800 leading-relaxed text-base cursor-pointer">
                    <strong className="text-green-800">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.AGREEMENT.ACKNOWLEDGMENT}</strong> 
                    <br /><br />
                    <span className="text-sm text-gray-600">
                      {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.AGREEMENT.AUTHORITY_CONFIRMATION} <strong>{partnerName}</strong> and that all information provided is accurate and complete.
                    </span>
                  </label>
                </div>
                {isAgreed && (
                  <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded text-sm text-green-800 flex items-center space-x-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <span>Great! You can now generate and complete the Partnership Agreement document.</span>
                  </div>
                )}
              </div>
            </section>

            <section className="mb-10 p-6 bg-white border-l-4 border-gray-500">
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.TITLE}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900 mb-2">{PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.COMPANY_NAME}</p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.LABELS.DATE}
                      </label>
                      <div className="h-8 border-b border-gray-300 text-gray-600 text-sm flex items-center">
                        {currentDate}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <p className="font-semibold text-gray-900 mb-2">{partnerName}</p>
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded">
                    <div className="flex items-center space-x-2">
                      <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                        isAgreed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                      }`}>
                        {isAgreed && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-gray-700">
                        {isAgreed ? 'Agreed to Terms' : 'Not Agreed'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {PARTNERSHIP_AGREEMENT_CONSTANTS.SECTIONS.SIGNATURES.LABELS.DATE}
                      </label>
                      <div className="h-8 border-b border-gray-300 text-gray-600 text-sm flex items-center">
                        {currentDate}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
              </>
            )}

            <div className="mt-12 p-6 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">{PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.TITLE}</h3>
              <p className="text-gray-700 mb-2">
                {PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.INTRO}
              </p>
              <p className="text-gray-700">
                <strong>{PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.COMPANY_NAME}</strong><br />
                Email: {PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.EMAIL}<br />
                Phone: {PARTNERSHIP_AGREEMENT_CONSTANTS.CONTACT.PHONE}
              </p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t px-6 py-4">
          {uploadStatus && (
            <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2">
                {isUploading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                )}
                <span className="text-sm text-blue-700 font-medium">{uploadStatus}</span>
              </div>
              {isUploading && (
                <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-progress-pulse" style={{width: '60%'}}></div>
                </div>
              )}
            </div>
          )}
          <div className="flex justify-end space-x-4">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
            {!existingDocument && (
              <button
                onClick={handleGeneratePDF}
                data-partnership-pdf-button
                disabled={!isAgreed || isUploading}
                className={`px-4 py-2 rounded-md transition-colors ${
                  isAgreed && !isUploading
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isUploading 
                  ? 'Uploading to S3...' 
                  : isAgreed 
                    ? 'Complete Step & Generate PDF' 
                    : 'Agree to Terms First'
                }
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnershipAgreementModal;
